import { useCallback, useMemo } from 'react';
import { Select, SelectOptionType } from '@octano/global-ui';
import { Control, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import RemovableChip from '../chip/RemovableChip';

// Valor centinela de la opción "Todos": selecciona todas las opciones de una vez.
// No colisiona con los valores reales (ids numéricos).
const SELECT_ALL_VALUE = '__ALL__';

interface ChipMultiSelectProps {
  name: string;
  label: string;
  options: SelectOptionType[];
  control: Control<any>;
  placeholder?: string;
  rules?: Record<string, any>;
  hideChips?: boolean;
  // Antepone una opción "Todos" que selecciona todas las opciones disponibles.
  allowSelectAll?: boolean;
}

const ChipMultiSelect = ({
  name,
  label,
  options,
  control,
  placeholder,
  rules,
  hideChips = false,
  allowSelectAll = false,
}: ChipMultiSelectProps) => {
  const { t } = useTranslation();
  const {
    field: { value = [], onChange },
  } = useController({ name, control, rules });

  const selectedValues = useMemo(
    () => (value as SelectOptionType[]).map((v) => v.value),
    [value],
  );

  const availableOptions = useMemo(
    () => options.filter((opt) => !selectedValues.includes(opt.value)),
    [options, selectedValues],
  );

  // Opción "Todos" arriba del resto, solo mientras quede algo por seleccionar.
  const selectOptions = useMemo(() => {
    if (allowSelectAll && availableOptions.length > 0) {
      return [
        { value: SELECT_ALL_VALUE, label: t('common.forms.selectAll') },
        ...availableOptions,
      ];
    }
    return availableOptions;
  }, [allowSelectAll, availableOptions, t]);

  const handleAdd = useCallback(
    (option: SelectOptionType) => {
      // "Todos": reemplaza la selección por todas las opciones.
      if (option.value === SELECT_ALL_VALUE) {
        onChange([...options]);
        return;
      }
      const current = (value as SelectOptionType[]) || [];
      if (!current.some((v) => v.value === option.value)) {
        onChange([...current, option]);
      }
    },
    [value, onChange, options],
  );

  const handleRemove = useCallback(
    (index: number) => {
      const current = [...((value as SelectOptionType[]) || [])];
      current.splice(index, 1);
      onChange(current);
    },
    [value, onChange],
  );

  return (
    <div>
      <Select
        name={`${name}_selector`}
        label={label}
        options={selectOptions}
        value={null as any}
        onChange={(e: any) => handleAdd(e)}
        placeholder={placeholder}
      />
      {!hideChips && (value as SelectOptionType[])?.length > 0 && (
        <div
          className="w-full mb-3 d-flex flex-wrap align-items-center justify-content-start"
          style={{ gap: 5 }}
        >
          {(value as SelectOptionType[]).map(
            (item: SelectOptionType, i: number) => (
              <RemovableChip
                key={String(item.value)}
                value={i}
                onRemove={handleRemove}
              >
                {item.label}
              </RemovableChip>
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default ChipMultiSelect;
